<template>
	<div class="TicketOptionExternalSystemSbbv3" v-if="ticketOption">
		<div class="row">
			<Field :title="$t('text.sbbv3id1')">
				<v-text-field variant="outlined" class="mt-3" hide-details style="background-color:#ffffff"
					v-model="ticketOption.fields.external.id1" 
					:error-messages="sbbv3Id1Error"
				/>
			</Field>
			<Field :title="$t('text.sbbv3id2')">
				<v-text-field variant="outlined" class="mt-3" hide-details style="background-color:#ffffff"
					v-model="ticketOption.fields.external.id2" 
					:error-messages="sbbv3Id2Error"
				/>
			</Field>
		</div>
		<div class="row">
			<Field :title="$t('text.sbbv3id3')">
				<v-text-field variant="outlined" class="mt-3" hide-details style="background-color:#ffffff"
					v-model="ticketOption.fields.external.id3" 
					:error-messages="sbbv3Id3Error"
				/>
			</Field>
		</div>
		<IfFeatureFlag flag="FEATURE_RIGI_PD_CHANGES">
			<div class="row">
				<Field :title="$t('text.sbbv3Duration')" :helpText="$t('text.sbbv3DurationHelp')">
					<v-text-field variant="outlined" class="mt-3" hide-details style="background-color:#ffffff"
						v-model="ticketOption.fields.external.duration" 
						:placeholder="$t('text.sbbv3DurationPlaceholder')"
						:error-messages="sbbv3DurationError"
					/>
				</Field>
				<Field :title="$t('text.sbbv3ReductionClass')" :helpText="$t('text.sbbv3ReductionClassHelp')">
					<v-text-field variant="outlined" class="mt-3" hide-details style="background-color:#ffffff"
						v-model="ticketOption.fields.external.reductionClass" 
						:error-messages="sbbv3ReductionClassError"
					/>
				</Field>
			</div>
		</IfFeatureFlag>
	</div>
</template>

<script lang="ts">
import Common from '../../mixins/Common.vue'
import IfFeatureFlag from '../ifFeatureFlag/IfFeatureFlag.vue'

export default {
	mixins: [ Common ],
	components: { IfFeatureFlag },
	props: {
		ticketOption: Object,
		product: Object,
	},
	computed: {
		sbbv3Id1Error(): string {
			if (!this.userIsOperator && this.spTrustLevel <= 2) { return '' }
			return !this.ticketOption?.fields.external.id1?.length ? this.$t('text.externalSystemRequired') : ''
		},
		sbbv3Id2Error(): string {
			if (!this.userIsOperator && this.spTrustLevel <= 2) { return '' }
			return !this.ticketOption?.fields.external.id2?.length ? this.$t('text.externalSystemRequired') : ''
		},
		sbbv3Id3Error(): string {
			if (!this.userIsOperator && this.spTrustLevel <= 2) { return '' }
			return !this.ticketOption?.fields.external.id3?.length ? this.$t('text.externalSystemRequired') : ''
		},
		sbbv3DurationError(): string {
			if (!this.$store.state.featureFlags['FEATURE_RIGI_PD_CHANGES'] == true) { return '' }
			const currentDuration = this.ticketOption?.fields?.external?.duration
			
			// Check if this is an SBBv3 external system
			if (this.product?.fields?.ticket?.de?.fields?.external?.system !== 'SBBv3') {
				return ''
			}
			
			// Get all SBBv3 ticket options
			const allTicketOptions = this.product?.fields?.ticket?.de?.fields?.ticketOptions?.de || []
			const sbbv3TicketOptions = allTicketOptions.filter(option => 
				option.fields?.external && 
				(option.fields.external.id1 || option.fields.external.id2 || option.fields.external.id3)
			)
			
			// Check if any other SBBv3 ticket option has duration filled
			const hasOtherDurations = sbbv3TicketOptions.some(option => 
				option !== this.ticketOption && 
				option.fields?.external?.duration && 
				option.fields.external.duration.trim() !== ''
			)
			
			// Check if current option has duration filled
			const hasCurrentDuration = currentDuration && currentDuration.trim() !== ''
			
			// If any SBBv3 option has duration, all must have duration
			if (hasOtherDurations && !hasCurrentDuration) {
				return this.$t('text.sbbv3DurationRequiredWhenOthersHave')
			}
			
			// If current option has duration, validate format
			if (hasCurrentDuration) {
				// Validate ISO-8601 duration format (e.g., PT2H30M, P1DT3H, PT45M)
				const iso8601DurationRegex = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/
				
				if (!iso8601DurationRegex.test(currentDuration)) {
					return this.$t('text.sbbv3DurationInvalidFormat')
				}
			}
			
			return ''
		},
		sbbv3ReductionClassError(): string {
			if (!this.$store.state.featureFlags['FEATURE_RIGI_PD_CHANGES'] == true) { return '' }
			const currentReductionClass = this.ticketOption?.fields?.external?.reductionClass

			// Get all SBBv3 ticket options
			const allTicketOptions = this.product?.fields?.ticket?.de?.fields?.ticketOptions?.de || []
			const sbbv3TicketOptions = allTicketOptions.filter(option => 
				option.fields?.external && 
				(option.fields.external.id1 || option.fields.external.id2 || option.fields.external.id3)
			)
			
			const hasOtherReductionClasses = sbbv3TicketOptions.some(option => 
				option !== this.ticketOption && 
				option.fields?.external?.reductionClass && 
				option.fields.external.reductionClass.trim() !== ''
			)
			
			const hasCurrentReductionClass = currentReductionClass && currentReductionClass.trim() !== ''

			if (hasOtherReductionClasses && !hasCurrentReductionClass) {
				return this.$t('text.sbbv3ReductionClassRequiredWhenOthersHave')
			}
			
			return ''
		},
		valid() {
			return !this.sbbv3Id1Error && !this.sbbv3Id2Error && !this.sbbv3Id3Error && !this.sbbv3DurationError && !this.sbbv3ReductionClassError
		},
	},
	methods: {
		async setExternalSystem() {
			if (!this.ticketOption?.fields?.external) return
			
			this.ticketOption.fields.external = {
				id1: this.ticketOption.fields.external.id1 ?? '',
				id2: this.ticketOption.fields.external.id2 ?? '',
				id3: this.ticketOption.fields.external.id3 ?? '',
				duration: this.ticketOption.fields.external.duration ?? '',
				reductionClass: this.ticketOption.fields.external.reductionClass ?? '',
			}
		},
	},
	beforeMount() {
		// TODO: also check if id1 actually exists, unify with the init in setExternalSystem
		if (!this.ticketOption?.fields?.external) {
			if (this.ticketOption?.fields) {
				this.ticketOption.fields.external = {
					id1: '',
					id2: '',
					id3: '',
					duration: '',
					reductionClass: '',
				}
			}
		}
	},
	async mounted() {
		await this.setExternalSystem()
	},
}
</script>

<style scoped>
.row { display: flex; gap: 10px; width: 100%; }
.row > * { flex: 1 0; }
.row > .Field { border-left: none; padding: 0; }
</style>